import { Box } from "@mui/material";
import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";

import { scrollBarWidth } from "@/theme";

import { Footer } from "../footer";
import { Header } from "../header";
import { Sidebar } from "../sidebar";

export type BasePageProps = {
  children: React.ReactNode;
  showSidebar: boolean;
  showFooter?: boolean;
  customHeader?: React.ReactNode;
  fullHeight?: boolean;
};

export const BasePage = ({
  children,
  showSidebar,
  showFooter,
  customHeader,
  fullHeight = false,
}: BasePageProps): JSX.Element => {
  const scrollableContainerRef = useRef<HTMLDivElement | null>(null);
  const [hasVerticalScrollbar, setHasVerticalScrollbar] = useState<boolean>(false);

  useEffect(() => {
    if (!scrollableContainerRef.current) return;
    const resizeObserver = new ResizeObserver((entries) => {
      const first = entries[0];
      if (first) setHasVerticalScrollbar(first.target.scrollHeight > first.target.clientHeight);
    });
    resizeObserver.observe(scrollableContainerRef.current);
    return (): void => resizeObserver.disconnect();
  }, []);

  return (
    <Box sx={{ width: "100%", height: fullHeight ? "100vh" : "auto", display: "flex", flexDirection: "column" }}>
      {customHeader ? customHeader : <Header />}
      <Box sx={{ flex: 1, minHeight: 0, display: "flex", width: "100%" }}>
        {showSidebar && <Sidebar />}
        <Box
          ref={scrollableContainerRef}
          sx={{
            padding: `20px 20px 20px calc(20px + ${hasVerticalScrollbar ? scrollBarWidth : "0px"})`,
            ...(fullHeight ? { height: "100%", display: "flex", flexDirection: "column" } : { maxHeight: "100%" }),
            overflowY: "auto",
            flex: 1,
            width: "100%",
          }}
        >
          {children}
        </Box>
      </Box>
      {showFooter && <Footer />}
    </Box>
  );
};
